import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import IntakeProgressCard from '@/components/IntakeProgressCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Users, Target, TrendingUp, Award, DollarSign, AlertCircle, UserCheck, Clock } from 'lucide-react';

const INCENTIVE_AMOUNT = 5000; // ₹5000 per admission (fixed for now)

// Design tokens from logo colors
const COLORS = {
  primary: '#192650',      // Navy from logo
  accent: '#0DA891',       // Teal from logo
  accentLight: '#E8F7F5',  // Light teal
  background: '#F8FAFC',   // Neutral gray
  cardBg: '#FFFFFF',
  textSecondary: '#64748B',
  border: '#E2E8F0',
};

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'operating_head';
  const isTeamLeader = user?.role === 'team_leader';
  const isCounselor = user?.role === 'counselor';
  
  // Counsellor/Team Leader state
  const [loading, setLoading] = useState(true);
  const [counselorStats, setCounselorStats] = useState(null);
  const [incentiveData, setIncentiveData] = useState(null);
  const [showIncentiveBanner, setShowIncentiveBanner] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  
  // Auto-refresh ref
  const scrollPositionRef = useRef(0);
  const isRefreshingRef = useRef(false);

  // Fetch data
  const fetchData = useCallback(async (isRefresh = false) => {
    if (!user?.role) return;
    if (isRefresh) {
      isRefreshingRef.current = true;
      scrollPositionRef.current = window.scrollY;
    }
    
    try {
      // Counsellor/Team Leader fetches personal stats + incentive data
      const [statsRes, incentiveRes] = await Promise.all([
        apiClient.get('/dashboard/unified-stats'),
        ['counselor', 'team_leader', 'admin', 'operating_head'].includes(user.role)
          ? apiClient.get('/dashboard/counselor-incentives')
          : Promise.resolve({ data: null })
      ]);
      
      setCounselorStats(statsRes.data);
      setIncentiveData(incentiveRes.data);
      
      // Show incentive banner if there's a recent admission
      if (incentiveRes.data?.recent_admission) {
        setShowIncentiveBanner(true);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (!isRefresh) {
        toast.error('Error loading dashboard');
      }
    } finally {
      setLoading(false);
      
      // Restore scroll position after refresh
      if (isRefresh) {
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current);
          isRefreshingRef.current = false;
        }, 50);
      }
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds (silent refresh)
    const interval = setInterval(() => {
      fetchData(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  // Download daily report
  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const today = new Date().toLocaleDateString('en-CA');
      const params = new URLSearchParams({
        date: today,
        format: 'excel'
      });
      
      const response = await apiClient.get(`/reports/daily-report?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Activity_${today}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  // Only show loading skeleton on initial load when no data exists
  if (loading && !counselorStats) {
    return (
      <div className="space-y-4 p-4 md:p-6 min-h-screen" style={{ backgroundColor: COLORS.background }}>
        <div className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: COLORS.border }}></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: COLORS.border }}></div>
          ))}
        </div>
      </div>
    );
  }

  // Calculate total earned incentive (all-time admissions × incentive amount)
  const totalEarnedIncentive = (counselorStats?.core_kpis?.total_admissions || 0) * INCENTIVE_AMOUNT;

  // ==================== COUNSELLOR/TEAM LEADER DASHBOARD ====================
  return (
    <div className="space-y-4 p-4 md:space-y-5 md:p-6 min-h-screen" style={{ backgroundColor: COLORS.background }} data-testid="counselor-dashboard">
      {/* Incentive Banner (shows after admission) */}
      {showIncentiveBanner && incentiveData?.recent_admission && (
        <div 
          className="rounded-xl p-4 md:p-5 shadow-sm relative overflow-hidden"
          style={{ backgroundColor: '#FEF3C7', borderLeft: '4px solid #F59E0B' }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="text-3xl md:text-4xl">🎉</div>
              <div>
                <h2 className="text-sm md:text-base font-semibold" style={{ color: COLORS.primary }}>
                  Congratulations! You earned ₹{INCENTIVE_AMOUNT.toLocaleString()}!
                </h2>
                <p className="text-xs md:text-sm" style={{ color: '#92400E' }}>
                  For admission of {incentiveData.recent_admission.student_name}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setShowIncentiveBanner(false)}
              className="absolute top-2 right-2 sm:static text-amber-700 hover:text-amber-900 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Welcome Header - Clean Design */}
      <div 
        className="rounded-xl p-4 md:p-5 transition-all duration-300"
        style={{ backgroundColor: COLORS.primary }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-lg md:text-xl font-semibold text-white">Welcome, {user?.full_name?.split(' ')[0]}! 👋</h1>
          {isTeamLeader && (
            <Badge 
              className="text-xs px-2 py-0.5"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              👑 Team Leader
            </Badge>
          )}
        </div>
        <p className="text-sm text-white/70 mt-1">Here's your performance overview</p>
      </div>

      {/* Personal Stats - Compact Cards */}
      <div 
        className="gap-2"
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
        }}
      >
        <CompactStatCard
          title="My Leads"
          value={counselorStats?.core_kpis?.total_leads_assigned || 0}
          icon={<Users className="h-3.5 w-3.5" />}
          accentColor="#3B82F6"
        />
        <CompactStatCard
          title="Active"
          value={counselorStats?.core_kpis?.active_leads || 0}
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          accentColor="#06B6D4"
        />
        <CompactStatCard
          title="Prospects"
          value={counselorStats?.core_kpis?.total_prospects || 0}
          icon={<UserCheck className="h-3.5 w-3.5" />}
          accentColor="#8B5CF6"
        />
        <CompactStatCard
          title="Admissions"
          value={counselorStats?.core_kpis?.total_admissions || 0}
          icon={<Award className="h-3.5 w-3.5" />}
          accentColor={COLORS.accent}
        />
        <CompactStatCard
          title="Overdue"
          value={counselorStats?.additional?.total_overdue || 0}
          icon={<AlertCircle className="h-3.5 w-3.5" />}
          accentColor="#EF4444"
        />
      </div>

      {/* Company admission target + intake countdown — after the counsellor's overview */}
      <IntakeProgressCard showMine={true} />

      {/* Targets Section - Stacked on mobile, side by side on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {/* Personal Target */}
        <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }}>
          <CardHeader className="p-4" style={{ backgroundColor: COLORS.accentLight }}>
            <CardTitle className="flex items-center gap-2 text-sm md:text-base">
              <Target className="h-4 w-4 md:h-5 md:w-5" style={{ color: COLORS.accent }} />
              <span style={{ color: COLORS.primary }}>My Monthly Target</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm" style={{ color: COLORS.textSecondary }}>Progress</span>
              <span className="text-xl md:text-2xl font-bold" style={{ color: COLORS.accent }}>
                {incentiveData?.personal?.monthly_achieved || 0} / {incentiveData?.personal?.monthly_target || 5}
              </span>
            </div>
            <div className="w-full rounded-full h-2.5 md:h-3 overflow-hidden" style={{ backgroundColor: COLORS.border }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(((incentiveData?.personal?.monthly_achieved || 0) / (incentiveData?.personal?.monthly_target || 5)) * 100, 100)}%`,
                  backgroundColor: (incentiveData?.personal?.monthly_achieved || 0) >= (incentiveData?.personal?.monthly_target || 5) ? COLORS.accent : '#3B82F6'
                }}
              />
            </div>
            <p className="text-xs md:text-sm mt-2" style={{ color: COLORS.textSecondary }}>
              {(incentiveData?.personal?.monthly_achieved || 0) >= (incentiveData?.personal?.monthly_target || 5)
                ? '🎉 Target achieved!'
                : `${(incentiveData?.personal?.monthly_target || 5) - (incentiveData?.personal?.monthly_achieved || 0)} more to go`
              }
            </p>
          </CardContent>
        </Card>

        {/* Team Target */}
        {incentiveData?.team ? (
          <Card className="border shadow-sm overflow-hidden" style={{ borderColor: COLORS.border }}>
            <CardHeader className="p-4" style={{ backgroundColor: '#F5F3FF' }}>
              <CardTitle className="flex items-center gap-2 text-sm md:text-base">
                <Users className="h-4 w-4 md:h-5 md:w-5" style={{ color: '#8B5CF6' }} />
                <span style={{ color: COLORS.primary }}>Team Target - {incentiveData.team.team_name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Team Progress</span>
                <span className="text-xl md:text-2xl font-bold" style={{ color: '#8B5CF6' }}>
                  {incentiveData.team.team_achieved} / {incentiveData.team.team_target}
                </span>
              </div>
              <div className="w-full rounded-full h-2.5 md:h-3 overflow-hidden" style={{ backgroundColor: COLORS.border }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min((incentiveData.team.team_achieved / incentiveData.team.team_target) * 100, 100)}%`,
                    backgroundColor: incentiveData.team.team_achieved >= incentiveData.team.team_target ? COLORS.accent : '#8B5CF6'
                  }}
                />
              </div>
              <p className="text-xs md:text-sm mt-2" style={{ color: COLORS.textSecondary }}>
                {incentiveData.team.team_members} members × 5 = {incentiveData.team.team_target} target
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border shadow-sm" style={{ borderColor: COLORS.border, backgroundColor: '#F9FAFB' }}>
            <CardHeader className="p-4" style={{ backgroundColor: '#F3F4F6' }}>
              <CardTitle className="flex items-center gap-2 text-sm md:text-base" style={{ color: COLORS.textSecondary }}>
                <Users className="h-4 w-4 md:h-5 md:w-5" />
                Team Target
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-center py-4" style={{ color: COLORS.textSecondary }}>
                Not assigned to any team
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Total Earned Incentive Card - Clean Design */}
      <Card className="border shadow-sm overflow-hidden" style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB' }}>
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div 
                className="h-12 w-12 md:h-14 md:w-14 rounded-xl flex items-center justify-center shadow-sm transition-transform duration-200 hover:scale-105"
                style={{ backgroundColor: '#F59E0B' }}
              >
                <DollarSign className="h-6 w-6 md:h-7 md:w-7 text-white" />
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium" style={{ color: '#92400E' }}>Total Earned Incentive</p>
                <p className="text-2xl md:text-3xl font-bold" style={{ color: COLORS.primary }}>
                  ₹{totalEarnedIncentive.toLocaleString()}
                </p>
                <p className="text-[10px] md:text-xs" style={{ color: '#B45309' }}>
                  ₹{INCENTIVE_AMOUNT.toLocaleString()} × {counselorStats?.core_kpis?.total_admissions || 0} admissions
                </p>
              </div>
            </div>
            <div className="text-center sm:text-right rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}>
              <p className="text-xs" style={{ color: '#92400E' }}>This Month</p>
              <p className="text-lg md:text-xl font-bold" style={{ color: COLORS.primary }}>
                {incentiveData?.personal?.monthly_achieved || 0} admissions
              </p>
              <p className="text-xs" style={{ color: '#B45309' }}>
                ₹{(incentiveData?.personal?.monthly_incentive || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

// Compact Stat Card Component - Single Row Layout
const CompactStatCard = ({ title, value, icon, accentColor }) => {
  return (
    <Card 
      className="border shadow-sm cursor-default"
      style={{ borderColor: COLORS.border }}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-2.5">
        <div className="flex items-center gap-2">
          <div 
            className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <span style={{ color: accentColor }}>{icon}</span>
          </div>
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-lg font-bold" style={{ color: COLORS.primary }}>{value.toLocaleString()}</span>
            <span className="text-[10px] truncate" style={{ color: COLORS.textSecondary }}>{title}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Dashboard;
